// src/services/suppliersService.ts
import { fetchApi } from './api';
import { Product } from './productsService';

export type { Product };

export interface Supplier {
  id: string | number;  // Permitir tanto string como number para el ID
  code: string;
  name: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  address?: string;
  leadTime: number;
  leadTimeIsStatic?: boolean;
  agreedLeadTime: number;
  serviceLevel: number;
  serviceInTime: number;
  serviceInQuantity: number;
  economicImpact: number;
  productCount: number;
  maxPurchaseFrequency: number;
  notes?: string;
  nextManagementDate?: string | null;  // Formato: YYYY-MM-DD
  managementComment?: string;
  serviceDay?: number | null;  // 1=lunes, 5=viernes
  useCalendarLeadTime?: boolean;
  serviceWindows?: SupplierServiceWindow[];
  /** Override de cobertura ideal (días). Null = hereda el umbral de la organización. */
  idealCoverageDays?: number | null;
  ideal_coverage_days?: number | null;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
}

export interface SupplierServiceWindow {
  id?: number;
  serviceDay?: number;
  deliveryDay?: number | null;
  isActive?: boolean;
  service_day?: number;
  delivery_day?: number | null;
  is_active?: boolean;
}

export interface LeadTimeHistory {
  date: string;
  value: number;
  forecast: number | null;
}

export interface SupplierFilters {
  search?: string;
  minServiceLevel?: number;
  maxServiceLevel?: number;
}

// Cache with promise to handle concurrent calls
let suppliersCache: { 
  data: Supplier[] | null; 
  timestamp: number | null;
  promise: Promise<Supplier[]> | null;
} = {
  data: null,
  timestamp: null,
  promise: null
};

const CACHE_DURATION = 5000; // 5 segundos

// API functions for suppliers
export const suppliersService = {
  // Get all suppliers with optional filtering
  getSuppliers: async (filters?: SupplierFilters): Promise<Supplier[]> => {
    try {
      // Si hay filtros, hacer la llamada directamente sin cache
      if (filters) {
        return await suppliersService._fetchSuppliersFromAPI(filters);
      }

      // Si tenemos cache válido, devolverlo
      if (suppliersCache.data && suppliersCache.timestamp) {
        const now = Date.now();
        if (now - suppliersCache.timestamp < CACHE_DURATION) {
          console.log('Returning cached suppliers data');
          return suppliersCache.data;
        }
      }

      // Si ya hay una llamada en progreso, esperar a que termine
      if (suppliersCache.promise) {
        console.log('Waiting for ongoing API call...');
        return await suppliersCache.promise;
      }

      // Crear nueva llamada y guardarla en el cache
      console.log('Starting new API call...');
      suppliersCache.promise = suppliersService._fetchSuppliersFromAPI();

      try {
        const result = await suppliersCache.promise;
        
        // Guardar en cache
        suppliersCache.data = result;
        suppliersCache.timestamp = Date.now();
        suppliersCache.promise = null;
        
        console.log(`Cached ${result.length} suppliers`);
        return result;
      } catch (error) {
        // Limpiar promise en caso de error
        suppliersCache.promise = null;
        throw error;
      }
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      return [];
    }
  },

  /** Búsqueda en el servidor de una sola página (selectores): evita descargar el listado completo. */
  searchSuppliers: async (search: string, limit: number = 20): Promise<Supplier[]> => {
    const params: Record<string, string | number> = { page: 1, page_size: limit };
    const term = search.trim();
    if (term) params.search = term;
    const response = await fetchApi<{ results?: Supplier[] }>('suppliers/', { params });
    return response && Array.isArray(response.results) ? response.results : [];
  },

  // Internal method to fetch suppliers from API
  _fetchSuppliersFromAPI: async (filters?: SupplierFilters): Promise<Supplier[]> => {
    let allSuppliers: Supplier[] = [];
    let page = 1;
    let hasMore = true;
    const maxPages = 10; // Límite de seguridad para evitar bucles infinitos
    
    console.log('Fetching suppliers from API...');
    
    while (hasMore && page <= maxPages) {
      const params: Record<string, string | number> = {
        page: page,
        page_size: 50, // Usar el tamaño por defecto
      };
      
      if (filters) {
        if (filters.search) params.search = filters.search;
        if (filters.minServiceLevel !== undefined) params.min_service_level = filters.minServiceLevel;
        if (filters.maxServiceLevel !== undefined) params.max_service_level = filters.maxServiceLevel;
      }
      
      const response = await fetchApi<any>('suppliers/', { params });
      
      if (response && Array.isArray(response.results)) {
        allSuppliers = [...allSuppliers, ...response.results];
        hasMore = response.next !== null;
        page++;
        
        // Si no hay resultados en esta página, parar
        if (response.results.length === 0) {
          hasMore = false;
        }
      } else {
        hasMore = false;
      }
    }
    
    // Advertencia si se alcanzó el límite
    if (page > maxPages) {
      console.warn(`Reached maximum page limit (${maxPages}). There might be more suppliers.`);
    }
    
    return allSuppliers;
  },
  
  /**
   * Proveedor por ID. Por defecto `compact=1`: sin lista de productos ni historial de LT
   * (el retrieve completo puede ser muy pesado). Pasar `detail: true` para el payload completo.
   */
  getSupplier: async (
    id: string,
    options?: { detail?: boolean }
  ): Promise<Supplier | null> => {
    try {
      const params =
        options?.detail === true ? undefined : ({ compact: '1' } as Record<string, string>);
      return await fetchApi<Supplier>(`suppliers/${id}/`, { params });
    } catch (error) {
      console.error(`Error fetching supplier ${id}:`, error);
      return null;
    }
  },
  
  // Get top suppliers by service level
  getTopSuppliers: async (limit: number = 10): Promise<Supplier[]> => {
    try {
      const response = await fetchApi<any>('suppliers/top/', { params: { limit } });
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Error fetching top suppliers:', error);
      return [];
    }
  },
  
  // Get bottom suppliers by service level
  getBottomSuppliers: async (limit: number = 10): Promise<Supplier[]> => {
    try {
      const response = await fetchApi<any>('suppliers/bottom/', { params: { limit } });
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error('Error fetching bottom suppliers:', error);
      return [];
    }
  },
  
  // Get lead time history for a supplier or all suppliers
  getLeadTimeHistory: async (supplierId?: string | number, months: number = 12): Promise<{
    lead_time_history: LeadTimeHistory[];
    agreed_lead_time?: number;
    global_service_level?: number;
    }> => {
      try {
        const dateFrom = new Date();
        dateFrom.setMonth(dateFrom.getMonth() - months);
        
        // Construir la URL según el caso
        let url: string;
        if (supplierId) {
          url = `suppliers/${supplierId.toString()}/lead-time-history/`;  // Para un proveedor específico
        } else {
          url = 'suppliers/lead-time-history/';               // Para todos los proveedores
        }
          
        const response = await fetchApi<any>(url, {
          params: {}

        });
        // const response = await fetchApi<any>(url, {
        //   params: {
        //     date_from: dateFrom.toISOString().split('T')[0],
        //     date_to: new Date().toISOString().split('T')[0]
        //   }
        // });
        
        // Si es un proveedor específico, la respuesta es un array
        if (supplierId) {
          return {
            lead_time_history: response,
            agreed_lead_time: undefined,
            global_service_level: undefined
          };
        }
        
        // Si es para todos los proveedores, la respuesta incluye el lead time acordado promedio y el nivel de servicio global
        return {
          lead_time_history: response.lead_time_history || [],
          agreed_lead_time: response.agreed_lead_time,
          global_service_level: response.global_service_level
        };
      } catch (error) {
        console.error(`Error fetching lead time history${supplierId ? ` for supplier ${supplierId}` : ''}:`, error);
        return {
          lead_time_history: [],
          agreed_lead_time: undefined,
          global_service_level: undefined
        };
      }
    },
  
  // Get products supplied by a supplier
  getSupplierProducts: async (supplierId: string | number): Promise<Product[]> => {
    try {
      const response = await fetchApi<any>(`suppliers/${supplierId.toString()}/products/`);
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error(`Error fetching products for supplier ${supplierId}:`, error);
      return [];
    }
  },

  // Calculate global service level
  calculateGlobalServiceLevel: async (): Promise<number> => {
    try {
      const suppliers = await suppliersService.getSuppliers();
      if (suppliers.length === 0) return 0;
      
      const totalServiceLevel = suppliers.reduce((sum, s) => sum + s.serviceLevel, 0);
      return Math.floor(totalServiceLevel / suppliers.length);
    } catch (error) {
      console.error('Error calculating global service level:', error);
      return 0;
    }
  },

  // Calculate average lead time from all valid history data (dynamic)
  calculateAverageLeadTime: async (): Promise<number> => {
    try {
      // Obtener el historial de lead time global
      const historyData = await suppliersService.getLeadTimeHistory();
      const leadTimeHistory = historyData.lead_time_history;
      
      if (leadTimeHistory.length === 0) return 0;
      
      // CAMBIO: Obtener TODOS los valores válidos del historial (dinámico)
      const allValidValues = leadTimeHistory
        .map(h => h.value)
        .filter((value): value is number => value !== null && value !== undefined && typeof value === 'number');
      
      if (allValidValues.length === 0) return 0;
      
      // Calcular el promedio de todos los valores válidos
      const averageLeadTime = allValidValues.reduce((sum, value) => sum + value, 0) / allValidValues.length;
      
      return Math.round(averageLeadTime * 10) / 10; // Redondear a 1 decimal
    } catch (error) {
      console.error('Error calculating average lead time:', error);
      return 0;
    }
  },

  // Calculate total economic impact from suppliers array
  calculateTotalEconomicImpact: async (suppliers?: Supplier[]): Promise<number> => {
    try {
      // Si no se pasan proveedores, obtenerlos (para compatibilidad)
      const suppliersToUse = suppliers || await suppliersService.getSuppliers();
      return suppliersToUse.reduce((sum, s) => sum + s.economicImpact, 0);
    } catch (error) {
      console.error('Error calculating total economic impact:', error);
      return 0;
    }
  },

  // Clear suppliers cache
  clearSuppliersCache: (): void => {
    suppliersCache.data = null;
    suppliersCache.timestamp = null;
    suppliersCache.promise = null;
    console.log('Suppliers cache cleared');
  },

  // Update purchase frequency for all products of a supplier
  updatePurchaseFrequency: async (supplierId: string | number, purchaseFrequency: number): Promise<{
    success: boolean;
    message: string;
    updated_count: number;
    dlt_updated_count: number;
    suggested_quantities_updated?: number;
    supplier_name?: string;
    errors?: any[];
  }> => {
    try {
      const response = await fetchApi<any>('suppliers/update-purchase-frequency/', {
        method: 'POST',
        body: {
          supplier_id: supplierId,
          purchase_frequency: purchaseFrequency
        }
      });
      
      // Clear cache after update
      suppliersService.clearSuppliersCache();
      
      return response;
    } catch (error) {
      console.error('Error updating purchase frequency:', error);
      throw error;
    }
  },

  /** Lead time estático a nivel proveedor (todas las filas ProductSupplier + Supplier.lead_time). */
  updateSupplierLeadTime: async (
    supplierId: string | number,
    leadTime: number
  ): Promise<{
    success: boolean;
    message?: string;
    updated_count?: number;
    lead_time?: number;
    lead_time_is_static?: boolean;
  }> => {
    try {
      const response = await fetchApi<any>('suppliers/update-lead-time/', {
        method: 'POST',
        body: {
          supplier_id: supplierId,
          lead_time: leadTime,
        },
      });
      suppliersService.clearSuppliersCache();
      return response;
    } catch (error) {
      console.error('Error updating supplier lead time:', error);
      throw error;
    }
  },

  /** Quita LT estático de alcance proveedor y restaura cálculo automático. */
  clearSupplierStaticLeadTime: async (
    supplierId: string | number
  ): Promise<{
    success: boolean;
    message?: string;
    updated_count?: number;
    lead_time?: number;
    lead_time_is_static?: boolean;
  }> => {
    try {
      const response = await fetchApi<any>('suppliers/update-lead-time/', {
        method: 'POST',
        body: {
          supplier_id: supplierId,
          lead_time_is_static: false,
        },
      });
      suppliersService.clearSuppliersCache();
      return response;
    } catch (error) {
      console.error('Error clearing supplier static lead time:', error);
      throw error;
    }
  },

  /** Lead time estático de una fila producto–proveedor. */
  updateProductSupplierLeadTime: async (
    productSupplierId: number,
    leadTime: number
  ): Promise<{
    success: boolean;
    product_id?: number;
    supplier_id?: number;
    lead_time?: number;
    lead_time_is_static?: boolean;
  }> => {
    try {
      const response = await fetchApi<any>(
        `suppliers/product-suppliers/${productSupplierId}/lead-time/`,
        {
          method: 'PATCH',
          body: { lead_time: leadTime },
        }
      );
      suppliersService.clearSuppliersCache();
      return response;
    } catch (error) {
      console.error('Error updating product-supplier lead time:', error);
      throw error;
    }
  },

  /** Quita el flag estático de una fila (vuelve a automático / calendario si aplica). */
  clearProductSupplierStaticLeadTime: async (
    productSupplierId: number
  ): Promise<{
    success: boolean;
    product_id?: number;
    supplier_id?: number;
    lead_time?: number;
    lead_time_is_static?: boolean;
    lead_time_from_calendar?: boolean;
  }> => {
    try {
      const response = await fetchApi<any>(
        `suppliers/product-suppliers/${productSupplierId}/lead-time/`,
        {
          method: 'PATCH',
          body: { lead_time_is_static: false },
        }
      );
      suppliersService.clearSuppliersCache();
      return response;
    } catch (error) {
      console.error('Error clearing product-supplier static lead time:', error);
      throw error;
    }
  },

  // Get supplier stats (monthly series and global median, ~365d window)
  getSupplierStats: async (supplierId: string | number, warehouseId: number): Promise<{
    monthly_data: Array<{
      month: string;
      month_name: string;
      year: number;
      month_number: number;
      median_lead_time: number;
      orders_count: number;
    }>;
    median_lead_time_observed: number | null;
    supplier: {
      id: number;
      code: string;
      name: string;
      lead_time: number;
    };
    last_purchase_date: string | null;
  }> => {
    try {
      const response = await fetchApi<any>(`suppliers/${supplierId.toString()}/stats/`, {
        params: {
          warehouse_id: warehouseId
        }
      });
      
      return response;
    } catch (error) {
      console.error(`Error fetching supplier stats for supplier ${supplierId}:`, error);
      return {
        monthly_data: [],
        median_lead_time_observed: null,
        supplier: {
          id: 0,
          code: '',
          name: '',
          lead_time: 0
        },
        last_purchase_date: null
      };
    }
  },

  // Update supplier management fields
  updateSupplierManagement: async (
    supplierId: string | number,
    nextManagementDate?: string | null,
    managementComment?: string
  ): Promise<{
    success: boolean;
    message?: string;
    supplier?: Supplier;
  }> => {
    try {
      const body: any = {};
      if (nextManagementDate !== undefined) {
        body.next_management_date = nextManagementDate;
      }
      if (managementComment !== undefined) {
        body.management_comment = managementComment;
      }
      
      const response = await fetchApi<Supplier>(
        `suppliers/${supplierId.toString()}/update-management/`,
        {
          method: 'PATCH',
          body
        }
      );
      
      // Clear cache after update
      suppliersService.clearSuppliersCache();
      
      return {
        success: true,
        supplier: response
      };
    } catch (error) {
      console.error(`Error updating supplier management for ${supplierId}:`, error);
      return {
        success: false,
        message: 'Error al actualizar la gestión del proveedor'
      };
    }
  },

  /** Override de cobertura ideal del proveedor (SUP-ORD-014). null = hereda org. */
  updateSupplierIdealCoverage: async (
    supplierId: string | number,
    idealCoverageDays: number | null
  ): Promise<{
    success: boolean;
    message?: string;
    supplier?: Supplier;
  }> => {
    try {
      const response = await fetchApi<Supplier>(
        `suppliers/${supplierId.toString()}/ideal-coverage/`,
        {
          method: 'PATCH',
          body: { ideal_coverage_days: idealCoverageDays },
        }
      );
      suppliersService.clearSuppliersCache();
      return {
        success: true,
        supplier: response,
      };
    } catch (error) {
      console.error(`Error updating ideal coverage for ${supplierId}:`, error);
      return {
        success: false,
        message: 'Error al actualizar la cobertura ideal del proveedor',
      };
    }
  },

  updateServiceCalendar: async (
    supplierId: string | number,
    payload: {
      windows: Array<{
        service_day: number;
        delivery_day: number | null;
        is_active: boolean;
      }>;
      use_calendar_lead_time: boolean;
    }
  ): Promise<{
    success: boolean;
    message?: string;
    supplier?: Supplier;
  }> => {
    try {
      const response = await fetchApi<Supplier>(
        `suppliers/${supplierId.toString()}/service-calendar/`,
        {
          method: 'PATCH',
          body: payload,
        }
      );
      suppliersService.clearSuppliersCache();
      return {
        success: true,
        supplier: response,
      };
    } catch (error) {
      console.error(`Error updating service calendar for ${supplierId}:`, error);
      return {
        success: false,
        message: 'Error al actualizar el calendario del proveedor',
      };
    }
  }
};
